import { useState } from "react";
import { useSync } from "@/context/SyncContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { HardDriveDownload, RefreshCw, UploadCloud, Trash2, AlertTriangle } from "lucide-react";

// Recovery controls shown ONLY when an accepted photo could not be committed to IndexedDB and the
// bytes live solely in memory for this page session. Truthful: we never claim "saved on device".
export function PersistenceRecovery() {
  const { memoryOnly, retrySaveLocal, retryUploadMemory, downloadBackup, discardMemory } = useSync();
  const [confirmId, setConfirmId] = useState(null);
  const [busy, setBusy] = useState(null);
  if (!memoryOnly || memoryOnly.length === 0) return null;

  const act = async (id, fn) => { setBusy(id); try { await fn(id); } finally { setBusy(null); } };

  return (
    <Dialog open={true}>
      <DialogContent data-testid="persistence-recovery-dialog" className="max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#B91C1C]">
            <AlertTriangle size={18} /> Photo not saved in browser storage
          </DialogTitle>
        </DialogHeader>
        <DialogDescription className="text-sm text-[#4B5563]">
          TenantSeal could not save this pending photo in browser storage. Keep this page open, retry the upload, or download a backup copy.
        </DialogDescription>
        <div className="mt-2 space-y-3 max-h-[50vh] overflow-y-auto">
          {memoryOnly.map((m) => (
            <div key={m.client_upload_id} data-testid={`memory-only-${m.client_upload_id}`} className="rounded-xl border border-[#FECACA] bg-[#FEF2F2] p-3">
              <div className="text-xs font-medium text-[#7F1D1D] mb-2 truncate">{m.filename || m.client_upload_id} · in memory only</div>
              <div className="grid grid-cols-2 gap-2">
                <Button data-testid={`mem-retry-save-${m.client_upload_id}`} disabled={busy === m.client_upload_id} onClick={() => act(m.client_upload_id, retrySaveLocal)} size="sm" variant="outline" className="rounded-lg text-xs">
                  <RefreshCw size={13} className="mr-1" /> Retry saving
                </Button>
                <Button data-testid={`mem-retry-upload-${m.client_upload_id}`} disabled={busy === m.client_upload_id} onClick={() => act(m.client_upload_id, retryUploadMemory)} size="sm" className="rounded-lg text-xs bg-[#1D4ED8] hover:bg-[#1E40AF]">
                  <UploadCloud size={13} className="mr-1" /> Retry upload
                </Button>
                <Button data-testid={`mem-download-${m.client_upload_id}`} onClick={() => downloadBackup(m.client_upload_id)} size="sm" variant="outline" className="rounded-lg text-xs">
                  <HardDriveDownload size={13} className="mr-1" /> Download backup
                </Button>
                <Button data-testid={`mem-discard-${m.client_upload_id}`} onClick={() => setConfirmId(m.client_upload_id)} size="sm" variant="outline" className="rounded-lg text-xs border-[#FECACA] text-[#B91C1C]">
                  <Trash2 size={13} className="mr-1" /> Discard
                </Button>
              </div>
              {confirmId === m.client_upload_id && (
                <div data-testid={`mem-discard-confirm-${m.client_upload_id}`} className="mt-2 rounded-lg bg-white border border-[#FCA5A5] p-2 text-xs">
                  <p className="text-[#7F1D1D] mb-2">Discard this photo? This cannot be undone and the photo will be lost.</p>
                  <div className="flex gap-2">
                    <Button data-testid={`mem-discard-yes-${m.client_upload_id}`} onClick={() => { discardMemory(m.client_upload_id); setConfirmId(null); }} size="sm" className="rounded-lg text-xs bg-[#B91C1C] hover:bg-[#991B1B]">Yes, discard</Button>
                    <Button data-testid={`mem-discard-no-${m.client_upload_id}`} onClick={() => setConfirmId(null)} size="sm" variant="outline" className="rounded-lg text-xs">Keep</Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        <p className="text-[11px] text-[#9CA3AF] mt-1">Leaving this page may lose photos held only in memory. (Exit warnings aren't guaranteed on every mobile browser.)</p>
      </DialogContent>
    </Dialog>
  );
}
